import {
  useSite,
  useSiteSetupActions,
} from "@inrange/building-manager-api-client";
import {
  SiteAllowStringValues,
  SiteElectricalSystem,
} from "@inrange/building-manager-api-client/models-site";
import { useEffect, useMemo, useState } from "react";
import { Card, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useSearchParams } from "react-router-dom";
import Loading from "../../Loading";
import ESysSelect from "../siteSections/ESysSelect";
import MonitoringPortalsCard from "../siteSections/MonitoringPortalsCard";
import SiteReadingsProcessed from "./SiteReadingsProcessed";
import SiteReadingsRaw from "./SiteReadingsRaw";
import SiteReadingsRawState from "./SiteReadingsRawState";
import { PresetName, RawPresetName } from "./types";

const SiteReadings = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize dataMode from URL query string or default to "processed"
  const initialDataMode = (() => {
    const mode = searchParams.get("dataMode");
    if (mode === "raw" || mode === "processed" || mode === "raw_state") {
      return mode;
    }
    return "processed";
  })();

  // Initialize from URL query string
  const initialStartDate = searchParams.get("startDate") || "";
  const initialEndDate = searchParams.get("endDate") || "";
  const initialAggregation = (searchParams.get("aggregation") || "none") as
    | "none"
    | "daily"
    | "weekly"
    | "monthly";
  const initialPreset = (() => {
    const p = searchParams.get("preset");
    if (
      p === "availability" ||
      p === "performance-ratio" ||
      p === "gen-vs-forecast" ||
      p === "demand-vs-forecast"
    )
      return p;
    return "default" as PresetName;
  })();
  const initialRawPreset = (() => {
    const p = searchParams.get("rawPreset");
    if (p === "inverter-temperature") return p;
    return "production-export" as RawPresetName;
  })();

  const [dataMode, setDataMode] = useState<"processed" | "raw" | "raw_state">(
    initialDataMode
  );
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);
  const [aggregationMode, setAggregationMode] = useState(initialAggregation);
  const [preset, setPreset] = useState<PresetName>(initialPreset);
  const [rawPreset, setRawPreset] = useState<RawPresetName>(initialRawPreset);
  const [selectedEsysIdx, setSelectedEsysIdx] = useState<string>(
    searchParams.get("esys") || ""
  );
  // Track whether URL params have been consumed (only use them on first load)
  const [urlParamsConsumed, setUrlParamsConsumed] = useState<boolean>(false);

  const { fetchSite } = useSite({
    siteId,
    app: "admin",
  });

  const site = fetchSite.data?.site as SiteAllowStringValues | undefined;

  const esysEntries = useMemo(() => {
    if (!site) return [];
    return Object.entries(site.esys).sort(
      ([, a], [, b]) =>
        (a as SiteElectricalSystem).idx - (b as SiteElectricalSystem).idx
    ) as [string, SiteElectricalSystem][];
  }, [site]);

  const esysUuids = useMemo(
    () => esysEntries.map(([, esys]) => esys.uuid),
    [esysEntries]
  );

  const { ingestionByEsys } = useSiteSetupActions({
    siteId: siteId!,
    esysUuids,
    enabled: esysUuids.length > 0,
  });

  // Initialize selectedEsysIdx when site data loads
  useEffect(() => {
    if (site && !selectedEsysIdx) {
      const esysList = Object.values(site.esys).filter((m) => !!m);
      if (esysList.length > 1) {
        setSelectedEsysIdx("");
      } else if (esysList.length === 1) {
        setSelectedEsysIdx(String(esysList[0].idx));
      }
    }
  }, [site, selectedEsysIdx]);

  // Update URL query string when dataMode, dates, aggregation, or forecast change
  useEffect(() => {
    setSearchParams(
      (params) => {
        params.set("dataMode", dataMode);
        if (startDate) {
          params.set("startDate", startDate);
        } else {
          params.delete("startDate");
        }
        if (endDate) {
          params.set("endDate", endDate);
        } else {
          params.delete("endDate");
        }
        if (aggregationMode !== "none") {
          params.set("aggregation", aggregationMode);
        } else {
          params.delete("aggregation");
        }
        if (preset !== "default") {
          params.set("preset", preset);
        } else {
          params.delete("preset");
        }
        // Only named raw presets are URL-serialisable; "custom" (hand-edited
        // selection) and the default clear the param.
        if (rawPreset === "inverter-temperature") {
          params.set("rawPreset", rawPreset);
        } else {
          params.delete("rawPreset");
        }
        if (selectedEsysIdx) {
          params.set("esys", selectedEsysIdx);
        } else {
          params.delete("esys");
        }
        return params;
      },
      { replace: true }
    );
  }, [
    dataMode,
    startDate,
    endDate,
    aggregationMode,
    preset,
    rawPreset,
    selectedEsysIdx,
    setSearchParams,
  ]);

  // Create efficient timezone formatters with cached Intl.DateTimeFormat instances
  const formatters = useMemo(() => {
    const timeZone = fetchSite.data?.site?.timeZone || "UTC";

    // Cache DateTimeFormat instances - creating these is expensive
    const dateTimeFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });

    const offsetFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    });

    const dateOnlyFormatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    // Cache for timezone offset - most data points will have the same offset
    const offsetCache = new Map<number, string>();

    const getOffset = (date: Date): string => {
      const hourKey = Math.floor(date.getTime() / 3600000);
      let offset = offsetCache.get(hourKey);
      if (offset === undefined) {
        const parts = offsetFormatter.formatToParts(date);
        const offsetPart = parts.find((part) => part.type === "timeZoneName");
        offset = offsetPart ? offsetPart.value : "+00:00";
        offsetCache.set(hourKey, offset);
      }
      return offset;
    };

    const formatWithOffset = (utcTimestamp: string | number) => {
      const date = new Date(utcTimestamp);
      const parts = dateTimeFormatter.formatToParts(date);

      const getPart = (type: string) =>
        parts.find((p) => p.type === type)?.value || "";

      const offset = getOffset(date);

      return `${getPart("year")}-${getPart("month")}-${getPart("day")} ${getPart("hour")}:${getPart("minute")} (${offset})`;
    };

    // Cache for shiftToSiteTimezone
    const shiftCache = new Map<number, number>();

    return {
      toLocaleString: formatWithOffset,
      toDateTimeString: formatWithOffset,
      toDateString: (utcTimestamp: string | number) => {
        const date = new Date(utcTimestamp);
        const parts = dateOnlyFormatter.formatToParts(date);
        const getPart = (type: string) =>
          parts.find((p) => p.type === type)?.value || "";
        return `${getPart("year")}-${getPart("month")}-${getPart("day")}`;
      },
      shiftToSiteTimezone: (utcTimestamp: string | number) => {
        const utcMs =
          typeof utcTimestamp === "number"
            ? utcTimestamp
            : new Date(utcTimestamp).getTime();

        const hourKey = Math.floor(utcMs / 3600000);

        let shiftMs = shiftCache.get(hourKey);
        if (shiftMs === undefined) {
          const date = new Date(utcMs);
          const parts = dateTimeFormatter.formatToParts(date);

          const getPart = (type: string) =>
            parseInt(parts.find((p) => p.type === type)?.value || "0", 10);

          const localMs = new Date(
            getPart("year"),
            getPart("month") - 1,
            getPart("day"),
            getPart("hour"),
            getPart("minute"),
            getPart("second")
          ).getTime();

          shiftMs = localMs - utcMs;
          shiftCache.set(hourKey, shiftMs);
        }

        return utcMs + shiftMs;
      },
      getTime: (utcTimestamp: string | number) => {
        return typeof utcTimestamp === "number"
          ? utcTimestamp
          : new Date(utcTimestamp).getTime();
      },
      dateRangeToTimestamps: (startDate: string, endDate: string) => {
        const [startYear, startMonth, startDay] = startDate
          .split("-")
          .map(Number);
        const [endYear, endMonth, endDay] = endDate.split("-").map(Number);

        const getMidnightUtc = (year: number, month: number, day: number) => {
          let utcMs = Date.UTC(year, month - 1, day, 0, 0, 0, 0);

          const getLocalParts = (ms: number) => {
            const parts = new Intl.DateTimeFormat("en-US", {
              timeZone,
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }).formatToParts(new Date(ms));

            const getPart = (type: string) =>
              parseInt(parts.find((p) => p.type === type)?.value || "0", 10);

            return {
              year: getPart("year"),
              month: getPart("month"),
              day: getPart("day"),
              hour: getPart("hour"),
              minute: getPart("minute"),
            };
          };

          const local = getLocalParts(utcMs);

          const targetUtc = Date.UTC(year, month - 1, day, 0, 0, 0, 0);
          const localUtc = Date.UTC(
            local.year,
            local.month - 1,
            local.day,
            local.hour,
            local.minute,
            0,
            0
          );

          const diff = localUtc - targetUtc;
          utcMs -= diff;

          return utcMs;
        };

        const startMs = getMidnightUtc(startYear, startMonth, startDay);
        const endMs = getMidnightUtc(endYear, endMonth, endDay + 1) - 1;

        return { startMs, endMs };
      },
      timeZone,
    };
  }, [fetchSite.data?.site?.timeZone]);

  // Get the selected esys UUID for filtering
  const selectedEsysUuid = useMemo(() => {
    if (!selectedEsysIdx || !site) return undefined;
    const esys = Object.values(site.esys)
      .filter((esys) => !!esys)
      .find((esys) => String(esys.idx) === selectedEsysIdx);
    return esys?.uuid;
  }, [selectedEsysIdx, site]);

  const handleDateChange = (newStartDate: string, newEndDate: string) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleUrlParamsConsumed = () => {
    setUrlParamsConsumed(true);
  };

  if (!fetchSite.isLoading && fetchSite.isError) {
    return <div>Error loading site data</div>;
  }

  if (fetchSite.isLoading || !site) {
    return <Loading label="Loading site data..." />;
  }

  return (
    <>
      <Container
        fluid
        style={{ padding: "12px 12px 0px 12px" }}
        className="d-flex flex-column gap-3"
      >
        <MonitoringPortalsCard
          esysEntries={esysEntries}
          ingestionByEsys={ingestionByEsys}
        />
        <Card body>
          <Row className="mb-3 mt-3">
            <Col sm={3}>
              <Form.Group>
                <Form.Label>Data</Form.Label>
                <Form.Select
                  value={dataMode}
                  onChange={(e) => {
                    const newMode = e.target.value as
                      | "processed"
                      | "raw"
                      | "raw_state";
                    setDataMode(newMode);
                    // Reset dates to force re-initialization with mode-specific defaults
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  <option value="processed">Processed readings</option>
                  <option value="raw">Raw readings</option>
                  {(site.measurementType === "reduxi" ||
                    site.measurementType === "solaredge") && (
                    <option value="raw_state">Raw device state</option>
                  )}
                </Form.Select>
              </Form.Group>
            </Col>

            <ESysSelect
              site={site}
              selectedEsysIdx={selectedEsysIdx}
              setSelectedEsysIdx={setSelectedEsysIdx}
              showWholeSiteOption={true}
            />
          </Row>

          {dataMode === "processed" && (
            <SiteReadingsProcessed
              site={site}
              siteId={siteId!}
              selectedEsysIdx={selectedEsysIdx}
              selectedEsysUuid={selectedEsysUuid}
              formatters={formatters}
              initialStartDate={initialStartDate}
              initialEndDate={initialEndDate}
              urlParamsConsumed={urlParamsConsumed}
              onUrlParamsConsumed={handleUrlParamsConsumed}
              onDateChange={handleDateChange}
              aggregationMode={aggregationMode}
              onAggregationChange={setAggregationMode}
              preset={preset}
              onPresetChange={setPreset}
            />
          )}

          {dataMode === "raw" && (
            <SiteReadingsRaw
              site={site}
              siteId={siteId!}
              selectedEsysUuid={selectedEsysUuid}
              formatters={formatters}
              initialStartDate={initialStartDate}
              initialEndDate={initialEndDate}
              urlParamsConsumed={urlParamsConsumed}
              onUrlParamsConsumed={handleUrlParamsConsumed}
              onDateChange={handleDateChange}
              rawPreset={rawPreset}
              onRawPresetChange={setRawPreset}
            />
          )}

          {dataMode === "raw_state" && (
            <SiteReadingsRawState
              siteId={siteId!}
              selectedEsysUuid={selectedEsysUuid}
              formatters={formatters}
              initialStartDate={initialStartDate}
              initialEndDate={initialEndDate}
              urlParamsConsumed={urlParamsConsumed}
              onUrlParamsConsumed={handleUrlParamsConsumed}
              onDateChange={handleDateChange}
            />
          )}
        </Card>
      </Container>
    </>
  );
};

export default SiteReadings;
